import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import {
  Card,
  Divider,
  Checkbox,
  Dialog,
  BUTTON_VARIANTS,
  SearchInput,
  INPUT_SIZES,
  TableHeader,
  MenuListItem,
  TextRenderer,
  TableContent,
  usePreventTableScrollingRef,
  TableContainer,
} from 'cdk-radial';
import { AgGridReact } from 'ag-grid-react';
import {
  setSelectedRequiredField,
  updateInitialSelectedFields,
  initialiseAPIFields,
  setSelectedApi,
  setApiFieldSearchInput,
} from '../../../../redux/connectedComponent/apiFields/apiFields.slice';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-balham.css';
import BodyContainer from '../bodyContainer/BodyContainer';
import classNames from 'classnames';
import './styles/EditApiFields.scss';

import './styles/SelectPrivacyProtection.scss';
import SelectedLocationsDialog from './sub-components/SelectedLocationsDialog';
import { requiredApiFieldsForSpec } from '../../../../constants/constants';
import { CONSTANTS } from '../../../subscriptionManagement/menuOptions/manageIntegrations/v2/constants';
import { ApiFieldNameTooltip } from '../../../UI/ApiFieldNameTooltip';

const TITLE = 'Edit API Fields';

export const CustomHeaderCheckbox = (props) => {
  const dispatch = useDispatch();
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const state = useSelector((state) => state);
  const {
    editFieldSelection,
    selectedApiDetails,
    apiFieldsMap,
    apiRequiredFieldsMap,
    apiFieldSearchInput,
  } = state && state?.apiFieldsSlice;
  const currentApiId = selectedApiDetails?.apiId;
  const currentApiName = selectedApiDetails?.apiName;
  const allFieldNames = apiFieldsMap[currentApiId] || [];
  const selectedFields = editFieldSelection[currentApiId] || [];
  const apiRequiredFields = apiRequiredFieldsMap[currentApiId] || [];
  const filteredRows = props?.api?.rowModel?.rowsToDisplay || [];
  const filteredFieldNames = filteredRows.map((row) => row.data.fieldName);

  const isAllSelected =
    apiFieldSearchInput && apiFieldSearchInput.length > 0
      ? filteredFieldNames.length > 0 &&
        filteredFieldNames.every((field) => selectedFields.includes(field))
      : allFieldNames.length > 0 &&
        allFieldNames.every((field) => selectedFields.includes(field));

  const handleSelectAll = (isChecked) => {
    if (isChecked) {
      // Select all fields
      dispatch(
        updateInitialSelectedFields({
          operation: 'enableAllFields',
          field:
            apiFieldSearchInput && apiFieldSearchInput.length > 0
              ? Array.from(new Set([...selectedFields, ...filteredFieldNames]))
              : allFieldNames,
          selectedApiId: currentApiId,
          apiName: currentApiName,
        })
      );
    } else {
      // Deselect all fields
      dispatch(
        updateInitialSelectedFields({
          operation: 'disableAllFields',
          field:
            apiFieldSearchInput && apiFieldSearchInput.length > 0
              ? selectedFields.filter(
                  (field) => !filteredFieldNames.includes(field)
                )
              : [],
          selectedApiId: currentApiId,
          apiName: currentApiName,
        })
      );
    }
  };

  const StyledDialog = styled(Dialog)`
    max-width: 510px;
  `;

  const handleClose = () => {
    setShowWarningDialog(false);
  };

  const handleChange = (event) => {
    const isChecked = event.target.checked;
    if (
      isChecked === false &&
      filteredFieldNames.some((field) => apiRequiredFields.includes(field))
    ) {
      setShowWarningDialog(true);
    } else {
      handleSelectAll(isChecked);
    }
  };

  const handleContinue = () => {
    setShowWarningDialog(false);
    handleSelectAll(false);
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <Checkbox
        checked={isAllSelected}
        onChange={handleChange}
        id="select-all-checkbox"
        name="select-all-checkbox"
        dataTestId="select-all-checkbox"
      />
      <StyledDialog
        isOpen={showWarningDialog}
        dataTestId="required-field-warning-dialog"
        id="required-field-warning-dialog"
        onClose={handleClose}
        title={'Warning'}
        buttonsProps={[
          {
            id: 'disable-continue-btn',
            dataTestId: 'disable-continue-btn',
            onClick: handleContinue,
            text: 'Continue',
            variant: BUTTON_VARIANTS.TEXT,
          },
        ]}
      >
        You have disabled all required fields. This action might cause your app
        to no longer function as intended. Do you wish to continue?
      </StyledDialog>
      <span>Fields</span>
    </div>
  );
};

export default function EditApiFields({ disableNextButton }) {
  const [gridApi, setGridApi] = useState(null);
  const [gridData, setGridData] = useState();
  const [data, setData] = useState([]);
  const [apiList, setApiList] = useState([]);
  const [totalRows, setTotalRows] = useState();
  const componentMainClass = 'apiEditFields__table';
  const agGridThemeClass = 'ag-theme-balham';
  const tableClass = classNames(componentMainClass, agGridThemeClass);
  const dispatch = useDispatch();

  const tableContentRef = usePreventTableScrollingRef();

  const frameworkComponentsExtended = {
    textRenderer: TextRenderer,
  };

  const state = useSelector((state) => state);
  const { selectedLocations } = state?.selectLocationsSlice;
  const { allLocations } = state && state?.loginDetailsSlice;
  const { verticalScopingApisList } = state && state?.selectDMSSlice;
  const { selectedApiDetails, apiFieldSearchInput } = state && state?.apiFieldsSlice;

  const selectedLocationsWithAddress =
    allLocations &&
    allLocations.filter(
      (f) => selectedLocations.length && selectedLocations.includes(f.entityId)
    );

  const getAllInstanceFieldMerged = (apiInstances) => {
    const uniqueFieldObj = {};
    for (const instance of apiInstances) {
      for (const fieldObj of instance) {
        let fieldName = fieldObj.name;
        let currentField = uniqueFieldObj[fieldName];
        // add key values to the uniqueFieldObj for 1st instance
        if (!currentField) {
          uniqueFieldObj[fieldName] = { ...fieldObj };
        } else {
          // if example is missing check for next instance and update
          if (!currentField.description && fieldObj.description) {
            currentField = { ...fieldObj };
          }
          if (fieldObj.description && fieldObj.example) {
            currentField = { ...fieldObj };
          }
          uniqueFieldObj[fieldName] = { ...currentField };
        }
      }
    }
    return Object.values(uniqueFieldObj);
  };

  useEffect(() => {
    if (verticalScopingApisList && verticalScopingApisList?.length) {
      const mapApiNamesAndFields = verticalScopingApisList.map((f) => {
        const apiInstances = Object.values(f?.apiFieldSelections?.instances);
        const mergeAllInstancesFields = getAllInstanceFieldMerged(apiInstances);
        //sorting api fields alphabetically before passing it to the AG Grid
        mergeAllInstancesFields.sort((a, b) => {
          const field1 = a?.name?.toUpperCase();
          const field2 = b?.name?.toUpperCase();

          if (field1 < field2) {
            return -1;
          }
          if (field1 > field2) {
            return 1;
          }
          return 0;
        });

        return {
          apiName: f.name,
          fields: mergeAllInstancesFields,
          apiType: f.apiType,
          apiId: f.id,
          manualSelectionFields: f.manualSelectionFields,
        };
      });

      const mapApiFields =
        Array.isArray(mapApiNamesAndFields) &&
        mapApiNamesAndFields.map((f) => {
          const filterRequiredFields =
            Array.isArray(f.fields) &&
            f.fields.filter(
              (field) => !requiredApiFieldsForSpec.has(field.name)
            );

          return { 
            ...f, 
            fields: filterRequiredFields 
          };
        });
      const gridDataForInitialRender = mapApiFields[0];
      dispatch(
        initialiseAPIFields({
          mapApiNamesAndFields: mapApiNamesAndFields,
        })
      );
      setGridData(gridDataForInitialRender);
      setApiList(mapApiFields);
      dispatch(setSelectedApi({apiName: gridDataForInitialRender.apiName, apiId: gridDataForInitialRender.apiId}));
    }
  }, [verticalScopingApisList]);

  useEffect(() => {
    if (gridData) {
      const apiName = gridData.apiName;
      const apiId = gridData.apiId;
      const apiType = gridData.apiType;
      let prepareGridData = [];
      gridData?.fields?.forEach((f) => {
        prepareGridData.push({
          apiName: apiName,
          fieldName: f.name,
          required: f.required,
          description: f?.description,
          example: f?.example,
          apiType: apiType,
          apiId: apiId,
        });
      });

      setData(prepareGridData);
    }
  }, [gridData]);

  const CheckboxSelector = ({ data }) => {
    const [showWarningDialog, setShowWarningDialog] = useState(false);

    const state = useSelector((state) => state);
    const { editFieldSelection } = state && state?.apiFieldsSlice;

    const StyledDialog = styled(Dialog)`
      max-width: 510px;
    `;

    const handleChange = (data) => (event) => {
      if (data?.required === true && event.target.checked === false) {
        setShowWarningDialog(true);
      } else {
        if (event.target.checked === true) {
          dispatch(
            updateInitialSelectedFields({
              operation: 'enableField',
              field: data?.fieldName,
              selectedApiId: data?.apiId,
              apiName: data?.apiName,
            })
          );
        }
        if (event.target.checked === false) {
          dispatch(
            updateInitialSelectedFields({
              operation: 'disableField',
              field: data?.fieldName,
              selectedApiId: data?.apiId,
              apiName: data?.apiName,
            })
          );
        }
      }
    };

    const handleClose = () => {
      setShowWarningDialog(false);
    };

    const handleContinue = () => {
      setShowWarningDialog(false);
      const fieldDetails = {
        apiId: data?.apiId,
        fieldName: data?.fieldName,
        apiName: data?.apiName,
      };
      dispatch(setSelectedRequiredField(fieldDetails));
      dispatch(
        updateInitialSelectedFields({
          operation: 'disableField',
          field: data?.fieldName,
          selectedApiId: data?.apiId,
          apiName: data?.apiName,
        })
      );
    };
    const isSelected = (data) => {
      return editFieldSelection[data?.apiId]?.includes(data.fieldName);
    };

    return (
      <>
        <Checkbox
          checked={isSelected(data)}
          onChange={handleChange(data)}
          id={`checkbox-${data?.fieldName}`}
          name={`checkbox-${data?.fieldName}`}
          dataTestId={`checkbox-${data?.fieldName}`}
        />
        <ApiFieldNameTooltip {...data} />
        <StyledDialog
          isOpen={showWarningDialog}
          id="dialog-3"
          onClose={handleClose}
          title={'Warning'}
          buttonsProps={[
            {
              id: 'dialog-3-action-1',
              onClick: handleContinue,
              text: 'Continue',
              variant: BUTTON_VARIANTS.TEXT,
            },
          ]}
        >
          You have disabled 1 required field. This action might cause your app
          to no longer function as intended. Do you wish to continue?
        </StyledDialog>
      </>
    );
  };
  
  const columns = [
    {
      cellRendererFramework: CheckboxSelector,
      field: 'required',
      headerComponentFramework: CustomHeaderCheckbox,
      width: 300,
      getQuickFilterText: params => {
        return params.data.fieldName || '';
      }
    },
  ];

  const handleSearchInputChange = (searchInput) => {
    dispatch(setApiFieldSearchInput(searchInput));

    if (gridApi) {
      gridApi.setQuickFilter(searchInput); // Refresh is needed for filtered cells highlighting to work properly

      gridApi.refreshCells({
        force: true,
      });
      const displayedRowsCount = gridApi.getDisplayedRowCount();
      setTotalRows(displayedRowsCount);

      if (searchInput && displayedRowsCount === 0) {
        gridApi.showNoRowsOverlay();
      } else {
        gridApi.hideOverlay();
      }
    }
  };

  const handleGridReady = (gridParams) => {
    setGridApi(gridParams.api);
    setTotalRows(gridParams.api.getDisplayedRowCount());
    gridParams.api.sizeColumnsToFit();
  };

  const getHeaderTitle = (value) => {
    return (
      <>
        <div className="api-name-header-container">
          <div className="api-name-header">
            <a>{selectedApiDetails.apiName}</a>
            <a className="api-name-header-subheading">
              All fields with * are required for optimal App function
            </a>
          </div>

          <div className="api-name-header-search-input">
            <SearchInput
              clearButtonLabel="clearButtonLabel"
              id="search-input-expanded"
              isAlwaysExpanded
              name="search-input-expanded"
              onChange={handleSearchInputChange}
              placeholder="Search Fields"
              size={INPUT_SIZES.STANDARD}
              value={apiFieldSearchInput}
            />
          </div>
        </div>
      </>
    );
  };
  function handleApiSelection(selectedApiName) {
    const selectedApiDetails =
      apiList && apiList.find((f) => f.apiName === selectedApiName);
    setGridData(selectedApiDetails);
    dispatch(setSelectedApi({apiName: selectedApiDetails.apiName, apiId: selectedApiDetails.apiId}));
    dispatch(setApiFieldSearchInput(''));
    if (gridApi) {
      gridApi.setQuickFilter('');
      gridApi.refreshCells({
        force: true,
      });
    }
  }
  function checkAllApiFieldsUnchecked(apis) {
    let selectedApiFields = {};

    for (const [apiId, fields] of Object.entries(apis)) {
      const filteredFields =
        (fields?.length &&
          fields.filter((f) => !requiredApiFieldsForSpec.has(f))) ||
        [];

      selectedApiFields[apiId] = filteredFields;
    }
    return Object.values(selectedApiFields).some((f) => f.length === 0);
  }
  useEffect(() => {
    if (checkAllApiFieldsUnchecked(state.apiFieldsSlice.editFieldSelection)) {
      disableNextButton(true);
    } else {
      disableNextButton(false);
    }
  }, [state.apiFieldsSlice.editFieldSelection]);
  return (
    <BodyContainer
      title={TITLE}
      description={
        <div>
          {`${CONSTANTS.MANAGE_API_FIELDS_DESCRIPTION}
        Configuration updates affect all`}
          <SelectedLocationsDialog
            selectedLocationsWithAddresses={selectedLocationsWithAddress}
          />
        </div>
      }
    >
      <div className="api-fields-selection-box-container">
        <Card className="api-fields-selection-card">
          <h3 className="api-list-heading">APIs List</h3>

          <Divider></Divider>

          <div className="api-fields-selection-box">
            <div className="api-name-list-container">
              {apiList.map((f) => (
                <MenuListItem
                  onClick={() => handleApiSelection(f.apiName)}
                  dataTestId="menu-list-item"
                  isSelected={selectedApiDetails.apiName === f?.apiName ? true : false}
                  hideSelectedItemCheckmark={true}
                >
                  <div className="api-name-and-fields-container">
                    <div className="api-name-container">{f.apiName}</div>
                    <span className="api-fields-count">{`Fields (${f?.fields?.length})`}</span>
                  </div>
                </MenuListItem>
              ))}
            </div>

            <Card className="api-fields-switch-selector">
              <TableContainer
                className={tableClass}
                tableLayout={'normal'}
                hideBorder
              >
                <TableHeader
                  headerTitle={getHeaderTitle()}
                  id="basic-table-header"
                  hideBorder
                  isDownloadable={false}
                  isPrintable={false}
                  isSearchable={false}
                />
                <Divider></Divider>
                <TableContent
                  data-testid="table-content"
                  ref={tableContentRef}
                  className="edit-api-fields-table"
                >
                  <AgGridReact
                    defaultColDef={{
                      filter: false,
                      sortable: true,
                    }}
                    frameworkComponents={frameworkComponentsExtended}
                    overlayNoRowsTemplate="No fields to show"
                    overlayLoadingTemplate={'Loading Fields...'}
                    columnDefs={columns}
                    rowHeight={30}
                    onGridReady={handleGridReady}
                    rowData={data}
                    disableStaticMarkup={true}
                  />
                </TableContent>
              </TableContainer>
            </Card>
          </div>
        </Card>
      </div>
    </BodyContainer>
  );
}
